import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  CheckCircleOutline as CheckCircleIcon,
} from '@mui/icons-material';
import { Elements, useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import StoreHeader from '../../components/layout/StoreHeader';
import StripeCardForm from '../../components/StripeCardForm';
import CardPreview from '../../components/CardPreview';
import CardCoverImage from '../../components/CardCoverImage';
import {
  getCart,
  clearCart,
  subscribeToCart,
  removeFromCartByIndex,
} from '../../services/cartService';
import apiClient from '../../lib/api';

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// IMPORTANT (perf phase 3): do NOT call loadStripe() at module scope. Calling it injects js.stripe.com
// immediately, which is how Stripe ended up on the BROWSE critical path. We memoize a single promise
// and only create it when the checkout view actually renders (getStripePromise() below), so Stripe.js
// loads strictly on /checkout — never on browse — even if this module is otherwise evaluated/prefetched.
let stripePromiseSingleton;
function getStripePromise() {
  if (!publishableKey) {
    return null;
  }
  if (!stripePromiseSingleton) {
    stripePromiseSingleton = loadStripe(publishableKey);
  }
  return stripePromiseSingleton;
}

/** US: exactly 5 digits. Other countries: at least 3 characters (loose). */
function isValidBillingPostalCode(billing) {
  const zip = (billing?.zip || '').trim();
  const country = (billing?.country || 'US').trim().toUpperCase();
  if (country === 'US') {
    return /^\d{5}$/.test(zip);
  }
  return zip.length >= 3;
}

function isBillingComplete(billing) {
  return Boolean(
    billing?.address?.trim() &&
      billing?.city?.trim() &&
      billing?.state?.trim()?.length >= 2 &&
      isValidBillingPostalCode(billing)
  );
}

const PaymentSection = ({ cartItems, billing, onBillingChange, taxPreview }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [lastOrderSummary, setLastOrderSummary] = useState(null);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [billingZipBlurred, setBillingZipBlurred] = useState(false);

  const billingCountry = (billing?.country || 'US').trim().toUpperCase();
  const zipInvalidAfterBlur = billingZipBlurred && !isValidBillingPostalCode(billing);
  const zipHelperText =
    zipInvalidAfterBlur &&
    (billingCountry === 'US' ? 'Enter a valid 5-digit ZIP code.' : 'Enter a valid postal code.');

  const billingReady = isBillingComplete(billing);
  const taxReady =
    billingReady &&
    !taxPreview.loading &&
    !taxPreview.error &&
    typeof taxPreview.subtotal === 'number' &&
    typeof taxPreview.tax === 'number' &&
    typeof taxPreview.total === 'number';

  const handlePlaceOrder = async () => {
    if (!stripe || !elements) {
      setOrderError('Stripe has not loaded yet. Please try again shortly.');
      return;
    }

    if (!billingReady) {
      setOrderError('Please complete your billing address.');
      return;
    }

    if (!taxReady) {
      setOrderError('Sales tax could not be calculated. Check your billing address and try again.');
      return;
    }

    setOrderError('');
    setIsPlacingOrder(true);

    try {
      const paymentIntentPayload = await apiClient.createPaymentIntent(cartItems, billing);
      if (!paymentIntentPayload?.success) {
        throw new Error(paymentIntentPayload?.message || 'Unable to initialize payment.');
      }

      const clientSecret = paymentIntentPayload.clientSecret;
      if (!clientSecret) {
        throw new Error('Unable to initialize payment. Please try again.');
      }

      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        throw new Error('Unable to access card details. Please refresh and try again.');
      }

      const confirmResult = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (confirmResult.error) {
        throw confirmResult.error;
      }

      const confirmedPaymentIntent = confirmResult.paymentIntent;
      if (!confirmedPaymentIntent) {
        throw new Error('Payment could not be confirmed.');
      }

      if (!['succeeded', 'processing', 'requires_capture'].includes(confirmedPaymentIntent.status)) {
        throw new Error(`Payment status "${confirmedPaymentIntent.status}" is not complete.`);
      }

      // Get obituaryId from localStorage if available
      const obituaryId = localStorage.getItem('obituaryId') || null;

      const orderResponse = await apiClient.createOrder({
        cart: cartItems,
        paymentIntentId: confirmedPaymentIntent.id,
        obituaryId: obituaryId,
      });

      if (!orderResponse?.success) {
        throw new Error(orderResponse?.message || 'Order creation failed.');
      }

      setLastOrderSummary({
        items: cartItems.map((item) => ({
          id: item.id,
          cardTitle: item.cardTitle,
          recipient: [item.firstName, item.lastName].filter(Boolean).join(' ') || item.name || 'Recipient',
          quantity: item.quantity,
        })),
        subtotal: orderResponse?.data?.subtotal,
        tax: orderResponse?.data?.tax,
        total: orderResponse?.data?.subtotal != null && orderResponse?.data?.tax != null
          ? Number((orderResponse.data.subtotal + orderResponse.data.tax).toFixed(2))
          : undefined,
        invoiceId: orderResponse?.data?.invoiceId,
      });

      clearCart();
      setSuccessModalOpen(true);
    } catch (error) {
      const message = error?.message || 'Unable to place order. Please verify your card details.';
      setOrderError(message);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{
        fontSize: { xs: '1rem', sm: '1.125rem' },
        mb: 2,
      }}>
        Billing address
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sales tax is calculated from your billing address.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <TextField
          fullWidth
          required
          label="Street address"
          value={billing.address}
          onChange={(e) => onBillingChange('address', e.target.value)}
        />
        <TextField
          fullWidth
          label="Apt, suite, etc. (optional)"
          value={billing.address2}
          onChange={(e) => onBillingChange('address2', e.target.value)}
        />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            required
            sx={{ flex: '1 1 160px' }}
            label="City"
            value={billing.city}
            onChange={(e) => onBillingChange('city', e.target.value)}
          />
          <TextField
            fullWidth
            required
            sx={{ flex: '0 1 88px' }}
            label="State"
            value={billing.state}
            onChange={(e) => onBillingChange('state', e.target.value.toUpperCase().slice(0, 2))}
            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
          />
          <TextField
            fullWidth
            required
            sx={{ flex: '1 1 120px' }}
            label="ZIP"
            value={billing.zip}
            onChange={(e) => onBillingChange('zip', e.target.value)}
            onBlur={() => setBillingZipBlurred(true)}
            error={zipInvalidAfterBlur}
            helperText={zipHelperText || undefined}
            inputProps={
              billingCountry === 'US'
                ? { maxLength: 5, inputMode: 'numeric', pattern: '[0-9]*' }
                : undefined
            }
          />
        </Box>
        <TextField
          fullWidth
          label="Country"
          value={billing.country}
          onChange={(e) => onBillingChange('country', e.target.value.toUpperCase().slice(0, 2))}
          inputProps={{ maxLength: 2 }}
          helperText="ISO code (e.g. US)"
        />
      </Box>

      {taxPreview.loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Calculating sales tax…
        </Typography>
      )}
      {taxPreview.error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {taxPreview.error}
        </Alert>
      ) : null}

      <Typography variant="subtitle1" gutterBottom sx={{
        fontSize: { xs: '1rem', sm: '1.125rem' },
        mb: 2,
      }}>
        Payment Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Provide your phone number and email if you would like to receive text or email updates about your order.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <TextField
          fullWidth
          label="Phone Number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(555) 123-4567"
          type="tel"
        />
        <TextField
          fullWidth
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
          type="email"
        />
      </Box>
      <StripeCardForm onError={(message) => setOrderError(message || '')} />
      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={handlePlaceOrder}
          disabled={
            cartItems.length === 0 ||
            isPlacingOrder ||
            !taxReady
          }
          fullWidth
          sx={{ minHeight: 48, fontSize: { xs: '0.9rem', sm: '0.95rem' } }}
        >
          {isPlacingOrder ? 'Processing…' : 'Place Order'}
        </Button>
        {orderError && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {orderError}
          </Typography>
        )}
      </Box>
      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
          <CheckCircleIcon sx={{ fontSize: 28 }} />
          Order Placed
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Thank you! Your payment was successful and we’ve started processing your order.
          </Typography>
          {(typeof lastOrderSummary?.subtotal === 'number' || typeof lastOrderSummary?.total === 'number') && (
            <Box sx={{ backgroundColor: 'grey.100', borderRadius: 1, p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Order summary
              </Typography>
              {typeof lastOrderSummary.subtotal === 'number' && (
                <Typography variant="body2" color="text.secondary">
                  Subtotal: ${lastOrderSummary.subtotal.toFixed(2)}
                </Typography>
              )}
              {typeof lastOrderSummary.tax === 'number' && (
                <Typography variant="body2" color="text.secondary">
                  Tax: ${lastOrderSummary.tax.toFixed(2)}
                </Typography>
              )}
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                Total: $
                {(typeof lastOrderSummary.total === 'number'
                  ? lastOrderSummary.total
                  : lastOrderSummary.subtotal
                ).toFixed(2)}
              </Typography>
              {lastOrderSummary.invoiceId && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Invoice ID: {lastOrderSummary.invoiceId}
                </Typography>
              )}
            </Box>
          )}
          {lastOrderSummary?.items?.length ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2">Recipients</Typography>
              {lastOrderSummary.items.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Recipient: {item.recipient}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Quantity: {item.quantity}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={() => setSuccessModalOpen(false)} fullWidth>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const emptyBilling = {
  address: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
};

const CartPage = () => {
  // Create the Stripe promise only now that the checkout view is mounting (perf phase 3) — this is
  // the single point where js.stripe.com is allowed to load.
  const stripePromise = useMemo(() => getStripePromise(), []);
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(true);
  const [cardImages, setCardImages] = useState({}); // cardId -> imageId mapping
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [billing, setBilling] = useState(emptyBilling);
  const [taxPreview, setTaxPreview] = useState({
    subtotal: null,
    tax: null,
    total: null,
    loading: false,
    error: '',
  });

  const getApiBaseUrl = () => apiClient.client.defaults.baseURL || 'http://localhost:8000';
  const getImageUrl = (cardId, imageId, size = 'preview') => {
    if (!cardId || !imageId) return undefined;
    const apiBaseUrl = getApiBaseUrl();
    // In development, fall back to full-size images (the WebP preview derivative isn't generated locally)
    const imageSize = process.env.NODE_ENV === 'development' ? 'full' : size;
    return `${apiBaseUrl}/api/cards/${cardId}/images/${imageId}/${imageSize}`;
  };

  // Fetch card images for all unique cardIds in cart
  useEffect(() => {
    
    if (cartLoading || cartItems.length === 0) {
      setCardImages({});
      return;
    }

    const fetchCardImages = async () => {
      const uniqueCardIds = [...new Set(cartItems.map(item => item.cardId).filter(Boolean))];
      
      if (uniqueCardIds.length === 0) {
        setCardImages({});
        return;
      }

      const imageMap = {};
      for (const cardId of uniqueCardIds) {
        try {
          const imagesResponse = await apiClient.getCardImages(cardId);
          if (imagesResponse?.success && imagesResponse?.data && imagesResponse.data.length > 0) {
            // Find the cover image (imageType === 'cover'), or fall back to first image
            const coverImage = imagesResponse.data.find((img) => img.imageType === 'cover') 
              || imagesResponse.data[0]; // Fallback to first image if no cover found
            const coverImageId = typeof coverImage === 'string' ? coverImage : coverImage.id;
            
            // Find inside1 background image
            const inside1Image = imagesResponse.data.find((img) => 
              (typeof img === 'object' && img.imageType === 'inside1')
            );
            const inside1ImageId = inside1Image ? (typeof inside1Image === 'string' ? inside1Image : inside1Image.id) : null;
            
            // Find inside2 background image
            const inside2Image = imagesResponse.data.find((img) => 
              (typeof img === 'object' && img.imageType === 'inside2')
            );
            const inside2ImageId = inside2Image ? (typeof inside2Image === 'string' ? inside2Image : inside2Image.id) : null;
            
            imageMap[cardId] = {
              cover: coverImageId,
              inside1: inside1ImageId,
              inside2: inside2ImageId,
            };
          }
        } catch (error) {
          // Silently handle image fetch errors
        }
      }
      
      setCardImages(imageMap);
    };

    fetchCardImages();
  }, [cartItems, cartLoading]);

  useEffect(() => {
    const items = getCart();
    setCartItems(items);
    setCartLoading(false);
    const unsubscribe = subscribeToCart((items) => {
      setCartItems(items);
    });
    return unsubscribe;
  }, []);

  const handleBillingChange = (field, value) => {
    setBilling((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (cartItems.length === 0) {
      setTaxPreview({ subtotal: null, tax: null, total: null, loading: false, error: '' });
      return undefined;
    }

    if (!isBillingComplete(billing)) {
      setTaxPreview({ subtotal: null, tax: null, total: null, loading: false, error: '' });
      return undefined;
    }

    const handle = setTimeout(async () => {
      setTaxPreview((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const res = await apiClient.previewCheckoutTax(cartItems, billing);
        if (!res?.success) {
          throw new Error(res?.message || 'Tax preview failed.');
        }
        setTaxPreview({
          subtotal: res.subtotal,
          tax: res.tax,
          total: res.total,
          loading: false,
          error: '',
        });
      } catch (e) {
        const message = e?.response?.data?.message || e?.message || 'Unable to calculate sales tax.';
        setTaxPreview({
          subtotal: null,
          tax: null,
          total: null,
          loading: false,
          error: message,
        });
      }
    }, 450);

    return () => clearTimeout(handle);
  }, [cartItems, billing]);


  const handleRemoveItem = (indexToRemove) => {
    removeFromCartByIndex(indexToRemove);
  };

  const handleClearCart = () => {
    clearCart();
  };

  const getItemUnitPrice = (item) => Number(item?.price) || 4.99;
  const getItemQuantity = (item) => Number(item?.quantity) > 0 ? Number(item.quantity) : 1;
  const getItemTotal = (item) => getItemUnitPrice(item) * getItemQuantity(item);

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + getItemTotal(item), 0);
  };

  const calculateTotal = () => {
    if (typeof taxPreview.total === 'number' && !taxPreview.loading && !taxPreview.error) {
      return taxPreview.total;
    }
    return calculateSubtotal();
  };

  const renderCartSection = () => (
    <Box
            sx={{ 
          display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 3, md: 3 }
      }}
    >
      <Card
        sx={{
          order: { xs: 1, md: 2 },
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              transform: 'none'
            },
            transition: 'none'
        }}
      >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ 
                fontSize: { xs: '1rem', sm: '1.125rem' }
              }}>
                Order Summary
              </Typography>
          {cartLoading ? (
            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' }, color: 'text.secondary', py: 2 }}>
              Loading cart…
            </Typography>
          ) : cartItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                Your cart is currently empty.
              </Typography>
              <Button variant="contained" onClick={() => { window.location.assign('/'); }}>
                Browse Cards
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                {cartItems.map((item, index) => {
                  const totalPrice = getItemTotal(item);
                  return (
                    <React.Fragment key={item.id || index}>
                      {index > 0 && <Divider sx={{ mb: 2 }} />}
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <Box
                          onClick={() => {
                            setPreviewItem(item);
                            setPreviewModalOpen(true);
                          }}
                          sx={{
                            width: 72,
                            height: 96,
                            backgroundColor: 'grey.100',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            overflow: 'hidden',
                            position: 'relative',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                        >
                          {/* WebP preview cover with full→placeholder fallback (shared with browse). */}
                          <CardCoverImage
                            cardId={item.cardId}
                            imageId={item.cardId ? cardImages[item.cardId]?.cover : null}
                            alt={item.cardTitle || 'Card thumbnail'}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              pointerEvents: 'none', // Let the Box handle clicks
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            <strong>Recipient:</strong> {[item.firstName, item.lastName].filter(Boolean).join(' ') || item.name || 'Recipient'}
                          </Typography>
                          {item.isFamilyMember ? (
                            <Alert severity="info" sx={{ mt: 1, mb: 1, py: 0.5 }}>
                              We already have the address, we have you covered.
                            </Alert>
                          ) : (
                            <>
                              {item.address && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  <strong>Address:</strong> {item.address}
                                  {item.address2 && `, ${item.address2}`}
                                </Typography>
                              )}
                              {(item.city || item.state || item.zip) && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  {[item.city, item.state, item.zip].filter(Boolean).join(', ')}
                                </Typography>
                              )}
                            </>
                          )}
                          {item.email && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              <strong>Email:</strong> {item.email}
                            </Typography>
                          )}
                          {/* Phone number hidden for privacy */}
                        </Box>
                        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            ${totalPrice.toFixed(2)}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveItem(index)}
                            startIcon={<DeleteIcon fontSize="small" />}
                            sx={{ textTransform: 'none' }}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Box>
                    </React.Fragment>
                  );
                })}
              </Box>
              <Button
                variant="outlined"
                color="error"
                onClick={handleClearCart}
                sx={{ alignSelf: 'flex-start', mb: 2 }}
              >
                Clear Cart
              </Button>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                <Typography variant="body2">${calculateSubtotal().toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Sales tax</Typography>
                <Typography variant="body2">
                  {taxPreview.loading ? '…' : taxPreview.error ? '—' : typeof taxPreview.tax === 'number' ? `$${taxPreview.tax.toFixed(2)}` : '—'}
                </Typography>
              </Box>
              {!isBillingComplete(billing) && cartItems.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Enter your billing address to calculate sales tax.
                </Typography>
              )}
              {taxPreview.error && (
                <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
                  {taxPreview.error}
                </Alert>
              )}
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}>Total</Typography>
                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, fontWeight: 600 }}>${calculateTotal().toFixed(2)}</Typography>
              </Box>
            </>
          )}
            </CardContent>
          </Card>
      <Card variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50', order: { xs: 2, md: 1 } }}>
        <Elements stripe={stripePromise}>
          <PaymentSection
            cartItems={cartItems}
            billing={billing}
            onBillingChange={handleBillingChange}
            taxPreview={taxPreview}
          />
        </Elements>
      </Card>
    </Box>
  );

  return (
    <>
      {/* Header */}
      <StoreHeader 
        cartCount={cartItems.length}
      />

      <Container maxWidth="lg" sx={{ 
        py: { xs: 2, sm: 4 }, 
        px: { xs: 2, sm: 3 },
        transition: 'none',
        '& *': {
          transition: 'none'
        }
      }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          Shopping Cart
        </Typography>

        {renderCartSection()}

      </Container>

      {/* Card Preview Modal */}
        <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Card Preview
          {previewItem && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              For: {[previewItem.firstName, previewItem.lastName].filter(Boolean).join(' ') || previewItem.name || 'Recipient'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <CardPreview
            previewItem={previewItem}
            cardImages={cardImages}
            getImageUrl={getImageUrl}
            onClose={() => setPreviewModalOpen(false)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CartPage;